// Currency formatting utilities

const CURRENCY_CONFIG = {
  'USD': { symbol: '$', name: 'US Dollar', position: 'before' },
  'MXN': { symbol: 'MX$', name: 'Mexican Peso', position: 'before' },
  'CAD': { symbol: 'C$', name: 'Canadian Dollar', position: 'before' },
  'EUR': { symbol: '€', name: 'Euro', position: 'before' },
  'GBP': { symbol: '£', name: 'British Pound', position: 'before' },
  'JPY': { symbol: '¥', name: 'Japanese Yen', position: 'before' },
  'CNY': { symbol: '¥', name: 'Chinese Yuan', position: 'before' },
  'INR': { symbol: '₹', name: 'Indian Rupee', position: 'before' },
  'BRL': { symbol: 'R$', name: 'Brazilian Real', position: 'before' },
  'ARS': { symbol: '$', name: 'Argentine Peso', position: 'before' },
  'AUD': { symbol: 'A$', name: 'Australian Dollar', position: 'before' }
};

/**
 * Format currency amount based on user's currency preference
 * @param {number} amount - The amount to format
 * @param {string} currencyCode - Currency code (e.g., 'USD', 'EUR')
 * @param {object} options - Formatting options
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount, currencyCode = 'USD', options = {}) => {
  const {
    showSymbol = true,
    showCode = false,
    decimals = 0,
    locale = 'en-US'
  } = options;

  if (amount === null || amount === undefined || isNaN(amount)) {
    return showSymbol ? `${CURRENCY_CONFIG[currencyCode]?.symbol || '$'}0` : '0';
  }

  const config = CURRENCY_CONFIG[currencyCode] || CURRENCY_CONFIG['USD'];
  
  // Format the number with locale-specific formatting
  const formattedAmount = new Intl.NumberFormat(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Math.abs(amount));

  let result = '';
  
  if (showSymbol) {
    result = `${config.symbol}${formattedAmount}`;
  } else {
    result = formattedAmount;
  }

  if (showCode) {
    result += ` ${currencyCode}`;
  }

  // Handle negative numbers
  if (amount < 0) {
    result = `-${result}`;
  }

  return result;
};

/**
 * Get currency symbol for a given currency code
 * @param {string} currencyCode - Currency code
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (currencyCode = 'USD') => {
  return CURRENCY_CONFIG[currencyCode]?.symbol || '$';
};

/**
 * Get currency name for a given currency code
 * @param {string} currencyCode - Currency code
 * @returns {string} Currency name
 */
export const getCurrencyName = (currencyCode = 'USD') => {
  return CURRENCY_CONFIG[currencyCode]?.name || 'US Dollar';
};

/**
 * Get all available currencies
 * @returns {Array} Array of currency objects
 */
export const getAllCurrencies = () => {
  return Object.entries(CURRENCY_CONFIG).map(([code, config]) => ({
    code,
    symbol: config.symbol,
    name: config.name
  }));
};